import React, { useState, useRef } from "react";
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Dropdown,
  Menu,
  Input,
  Space,
} from "antd";
import { EditOutlined, SearchOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWorkers, deleteWorker } from "../services/workersAPI";
import { useAuth } from "../hooks/useAuth";
import CreateWorkerModal from "../components/CreateWorkerModal";
import Highlighter from "react-highlight-words";
import { formatDateTimeLocal } from "../utils/dateUtils";
import ViewWorkerModal from "../components/ViewWorkerModal";
import UpdateWorkerModal from "../components/UpdateWorkerModal";

const formatDataSource = (dataSource) => {
  const mapping = {
    rfi: "RFI",
    submittal: "Submittal",
    daily_report: "Daily Report",
  };
  return mapping[dataSource] || dataSource;
};

const formatFrequency = (frequency) => {
  return frequency.charAt(0).toUpperCase() + frequency.slice(1);
};

const Workers = () => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const userRole = selectedProject.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  const {
    data: workers,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["workers", projectId],
    queryFn: () => fetchWorkers(projectId),
    enabled: !!projectId,
  });

  const queryClient = useQueryClient();

  if (isError) {
    notification.error({
      message: "Failed to load workers",
      description: error.message,
    });
  }

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const getColumnSearchProps = (dataIndex, customRender) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, confirm)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1677ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={
            customRender ? customRender(text).toString() : text?.toString()
          }
        />
      ) : customRender ? (
        customRender(text)
      ) : (
        text
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteWorker,
    onSuccess: () => {
      queryClient.invalidateQueries(["workers", projectId]);
      notification.success({ message: "Worker deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete worker",
        description: error.message,
      });
    },
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Data Source",
      dataIndex: "data_source",
      key: "data_source",
      ...getColumnSearchProps("data_source", formatDataSource),
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      ...getColumnSearchProps("frequency", formatFrequency),
    },
    {
      title: "Next Run",
      dataIndex: "next_run_date",
      key: "next_run_date",
      render: (text) => (text ? formatDateTimeLocal(text) : ""),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="1"
                onClick={() => {
                  setSelectedWorker(record);
                  setIsViewModalVisible(true);
                }}
              >
                <Button className="w-full">View</Button>
              </Menu.Item>
              {isOwnerOrManager && (
                <>
                  <Menu.Item
                    key="2"
                    onClick={() => {
                      setSelectedWorker(record);
                      setIsUpdateModalVisible(true);
                    }}
                  >
                    <Button className="w-full">Update</Button>
                  </Menu.Item>
                  <Menu.Item key="3">
                    <Popconfirm
                      title="Are you sure you want to delete this worker?"
                      onConfirm={() => deleteMutation.mutate(record._id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button className="w-full" danger>
                        Delete
                      </Button>
                    </Popconfirm>
                  </Menu.Item>
                </>
              )}
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<EditOutlined />} size="medium" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">AI Workers</h1>
      <div className="flex gap-4 mb-4">
        {isOwnerOrManager && (
          <Button type="primary" onClick={() => setIsCreateModalVisible(true)}>
            Create
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={workers}
        rowKey="_id"
        loading={isLoading}
        pagination={{ pageSize: 8 }}
      />
      <CreateWorkerModal
        visible={isCreateModalVisible}
        onClose={() => setIsCreateModalVisible(false)}
      />
      <ViewWorkerModal
        visible={isViewModalVisible}
        onClose={() => {
          setIsViewModalVisible(false);
          setSelectedWorker(null);
        }}
        workerId={selectedWorker?._id}
      />
      <UpdateWorkerModal
        visible={isUpdateModalVisible}
        onClose={() => {
          setIsUpdateModalVisible(false);
          setSelectedWorker(null);
        }}
        worker={selectedWorker}
      />
    </div>
  );
};

export default Workers;
